import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";

import { extractOrgIdFromPath } from "~/components/RouteOrgIdProvider";
import { useApolloClients } from "~/hooks/useApolloClients";
import { useAuthorization } from "~/hooks/useAuthorization";
import { useIsMounted } from "~/hooks/useIsMounted";
import { IntegrationConnectStart } from "~/lib/federationGateway/platformQueries/integrationsQueries";

import {
  INTEGRATION_CALLBACK_PATH,
  ESIGNATURE_SETTINGS_PATH,
} from "./constants";
import { integrationErrorMessage, throwIfRefused } from "./errors";
import type {
  IntegrationCategory,
  IntegrationMutationError,
  IntegrationProvider,
} from "./types";

/**
 * Remembers a connection in flight across the trip to the provider and back.
 * The provider returns to one fixed address with no organisation in it, so
 * this is how the callback knows which organisation to finish in and where
 * to take the person afterwards. One slot: a tab can only be away at one
 * provider at a time.
 */
export const INTEGRATION_CONNECT_SESSION_KEY = "compono.integrations.connect";

export interface IntegrationConnectSession {
  /** The organisation the connection was started in — and must finish in. */
  organisationId: string;
  /** The in-app path to come back to, e.g. the offer the person was sending. */
  returnTo: string;
  category: IntegrationCategory;
  provider: IntegrationProvider;
  /**
   * The OAuth `state` the provider will hand back, which ties a callback to
   * this attempt. Absent only if the consent page carried none.
   */
  state?: string;
  /**
   * When the completion was sent. The state is single-use, so a page that
   * reloads after this asks how the attempt went rather than sending it again.
   */
  submittedAt?: string;
}

/** The address registered with every provider as the place to come back to. */
export const integrationRedirectUri = (): string =>
  `${window.location.origin}${INTEGRATION_CALLBACK_PATH}`;

/** Settings → E-signature in an organisation: where a connection returns by default. */
export const eSignatureSettingsPath = (organisationId: string): string =>
  `/organisation/${organisationId}${ESIGNATURE_SETTINGS_PATH}`;

/** A path inside this app — never `//host`, which a browser reads as another site. */
export const isInAppPath = (path: unknown): path is string =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith("/\\");

const isOrganisationId = (value: unknown): value is string =>
  typeof value === "string" &&
  extractOrgIdFromPath(`/organisation/${value}`) === value;

const isFilled = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const stateOf = (authorizeUrl: string): string | undefined => {
  try {
    return new URL(authorizeUrl).searchParams.get("state") ?? undefined;
  } catch {
    return undefined;
  }
};

/** Returns false when the browser refuses the write (storage disabled or full). */
export function writeConnectSession(
  session: IntegrationConnectSession,
): boolean {
  try {
    window.sessionStorage.setItem(
      INTEGRATION_CONNECT_SESSION_KEY,
      JSON.stringify(session),
    );
    return true;
  } catch {
    return false;
  }
}

/** The connection in flight, or null when there is none or it can't be trusted. */
export function readConnectSession(): IntegrationConnectSession | null {
  try {
    const raw = window.sessionStorage.getItem(INTEGRATION_CONNECT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>> | null;
    if (
      !parsed ||
      !isOrganisationId(parsed.organisationId) ||
      !isInAppPath(parsed.returnTo) ||
      !isFilled(parsed.category) ||
      !isFilled(parsed.provider)
    ) {
      return null;
    }
    return {
      organisationId: parsed.organisationId,
      returnTo: parsed.returnTo,
      category: parsed.category as IntegrationCategory,
      provider: parsed.provider as IntegrationProvider,
      ...(isFilled(parsed.state) ? { state: parsed.state } : {}),
      ...(isFilled(parsed.submittedAt)
        ? { submittedAt: parsed.submittedAt }
        : {}),
    };
  } catch {
    return null;
  }
}

export function clearConnectSession(): void {
  try {
    window.sessionStorage.removeItem(INTEGRATION_CONNECT_SESSION_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/**
 * What the provider sent back to the callback, held off the URL. A `code` and
 * `state` left in the address bar are taken by the sign-in library for an
 * Auth0 callback of its own; it fails to match them and drops the signed-in
 * user, which costs a full redirect through Auth0 on the way back in.
 */
export const INTEGRATION_CALLBACK_PARAMS_KEY = "compono.integrations.callback";

export interface IntegrationCallbackParams {
  code: string | null;
  state: string | null;
  /** Set when the provider declined or failed, e.g. `access_denied` for Cancel. */
  error: string | null;
}

export const callbackParamsFrom = (
  search: string,
): IntegrationCallbackParams => {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
  };
};

/** `search` without the provider's answer; empty when nothing else is left. */
export const withoutCallbackParams = (search: string): string => {
  const params = new URLSearchParams(search);
  ["code", "state", "error"].forEach((name) => params.delete(name));
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

/** Returns false when the browser refuses the write. */
export function writeCallbackParams(
  params: IntegrationCallbackParams,
): boolean {
  try {
    window.sessionStorage.setItem(
      INTEGRATION_CALLBACK_PARAMS_KEY,
      JSON.stringify(params),
    );
    return true;
  } catch {
    return false;
  }
}

export function readCallbackParams(): IntegrationCallbackParams | null {
  try {
    const raw = window.sessionStorage.getItem(INTEGRATION_CALLBACK_PARAMS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>> | null;
    if (!parsed) return null;
    const text = (value: unknown) => (isFilled(value) ? value : null);
    return {
      code: text(parsed.code),
      state: text(parsed.state),
      error: text(parsed.error),
    };
  } catch {
    return null;
  }
}

export function clearCallbackParams(): void {
  try {
    window.sessionStorage.removeItem(INTEGRATION_CALLBACK_PARAMS_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export interface StartIntegrationConnectParams {
  category: IntegrationCategory;
  provider: IntegrationProvider;
  /**
   * The in-app path to return to once connected, organisation prefix included —
   * typically `${location.pathname}${location.search}`. Anything that isn't an
   * in-app path returns to Settings → E-signature instead.
   */
  returnTo: string;
}

/**
 * Starts connecting a provider: remembers the organisation and where to come
 * back to, asks the integrations service for the provider's consent page, and
 * sends the browser there. The provider returns to `/integrations/callback`,
 * which finishes the connection in that organisation and comes back to
 * `returnTo`.
 *
 * `start` never rejects — a failure lands in `error`, whose message is written
 * for the person and safe to show as it is — and a call while another is in
 * flight does nothing. `starting` stays true once the browser is on its way,
 * so a Connect button keeps its spinner until the page unloads. Owner/Admin
 * only: the service refuses anyone else.
 */
export function useStartIntegrationConnect(): {
  start: (params: StartIntegrationConnectParams) => Promise<void>;
  starting: boolean;
  error?: Error;
} {
  const { platformFederationGatewayClient: client } = useApolloClients();
  const { orgId } = useAuthorization();
  const isMounted = useIsMounted();
  const [startConnect] = useMutation<{
    integrationConnectStart: {
      authorizeUrl?: string | null;
      errors: IntegrationMutationError[];
    };
  }>(IntegrationConnectStart, { client });

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<Error>();
  // One attempt at a time. A second, from a double-click say, asks for another
  // consent page and overwrites the remembered `state`, so the provider can
  // come back with the first one's and find nothing to finish. A ref, because
  // the second call can land before `starting` re-renders.
  const inFlight = useRef(false);

  // Back from the provider's page can restore this one from the browser's
  // back-forward cache, mid-redirect; let its Connect button go again.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      inFlight.current = false;
      setStarting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const start = useCallback(
    async ({ category, provider, returnTo }: StartIntegrationConnectParams) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError(undefined);
      setStarting(true);

      const fail = (message: string) => {
        inFlight.current = false;
        clearConnectSession();
        if (!isMounted()) return;
        setError(new Error(message));
        setStarting(false);
      };

      const session: IntegrationConnectSession = {
        organisationId: orgId,
        returnTo: isInAppPath(returnTo)
          ? returnTo
          : eSignatureSettingsPath(orgId),
        category,
        provider,
      };
      // Written before the request as well as after, so a browser that
      // refuses storage stops here rather than at the far end of the trip.
      if (!writeConnectSession(session)) {
        fail(
          "Your browser wouldn't let Compono remember where to bring you back to. Allow this site to store data, then try again.",
        );
        return;
      }

      try {
        const { data } = await startConnect({
          variables: {
            input: {
              category,
              provider,
              redirectUri: integrationRedirectUri(),
            },
          },
        });
        throwIfRefused(data?.integrationConnectStart);
        const authorizeUrl = data?.integrationConnectStart.authorizeUrl;
        if (!authorizeUrl) throw new Error("No consent page was returned.");
        writeConnectSession({ ...session, state: stateOf(authorizeUrl) });
        window.location.assign(authorizeUrl);
      } catch (cause) {
        fail(
          integrationErrorMessage(
            cause,
            "Couldn't start connecting. Please try again.",
          ),
        );
      }
    },
    [orgId, startConnect, isMounted],
  );

  return { start, starting, error };
}
